import { useEffect } from 'react';

/**
 * [PURPOSE]: Global keyboard shortcuts for god-tier UX
 * [WHY]: Power users need quick access to common actions
 * [INTEGRATION]: Used by App.jsx to handle all keyboard shortcuts
 *
 * Supported shortcuts:
 * - H: Heal 1 HP
 * - D: Take 1 damage
 * - L: Open level-up wizard
 * - E: Toggle equipment panel
 * - S: Toggle spells panel
 * - I: Focus inventory search
 * - Escape: Close all modals
 * - ?: Show keyboard shortcuts help
 */

const useKeyboardShortcuts = (actions = {}, { enabled = false } = {}) => {
  useEffect(() => {
    // [GATE]: Opt-in only. When disabled, register no listener at all so
    // single keys never fire actions for users who didn't ask for hotkeys.
    if (!enabled) return undefined;

    const handleKeyPress = (e) => {
      // [IGNORE]: Don't trigger when typing in inputs
      if (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable) {
        // Exception: Allow Escape to blur inputs
        if (e.key === 'Escape') {
          e.target.blur();
        }
        return;
      }

      // [MODIFIER KEYS]: Require no modifiers (except Shift for ?)
      if (e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      const key = e.key.toLowerCase();

      switch (key) {
        case 'h':
          // Heal 1 HP
          e.preventDefault();
          actions.onHeal?.(1);
          break;

        case 'd':
          // Take 1 damage
          e.preventDefault();
          actions.onDamage?.(1);
          break;

        case 'l':
          // Open level-up wizard
          e.preventDefault();
          actions.onLevelUp?.();
          break;

        case 'e':
          // Toggle equipment panel
          e.preventDefault();
          actions.onToggleEquipment?.();
          break;

        case 's':
          // Toggle spells panel
          e.preventDefault();
          actions.onToggleSpells?.();
          break;

        case 'i':
          // Focus inventory search
          e.preventDefault();
          actions.onFocusInventory?.();
          break;

        case 'r':
          // Open rest panel
          e.preventDefault();
          actions.onRest?.();
          break;

        case 'j':
          // Open journal
          e.preventDefault();
          actions.onJournal?.();
          break;

        case '?':
          // Show keyboard shortcuts help
          e.preventDefault();
          actions.onShowHelp?.();
          break;

        case 'escape':
          // Close all modals/panels
          e.preventDefault();
          actions.onEscape?.();
          break;

        default:
          // No action for this key
          break;
      }
    };

    // [REGISTER]: Add event listener
    window.addEventListener('keydown', handleKeyPress);

    // [CLEANUP]: Remove on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [actions, enabled]);

  return null; // This hook doesn't render anything
};

export default useKeyboardShortcuts;

/**
 * [USAGE EXAMPLE]:
 *
 * import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
 *
 * function App() {
 *   const [hp, setHP] = useState(25);
 *
 *   useKeyboardShortcuts({
 *     onHeal: (amount) => setHP(prev => Math.min(prev + amount, maxHP)),
 *     onDamage: (amount) => setHP(prev => Math.max(prev - amount, 0)),
 *     onLevelUp: () => setShowLevelUpWizard(true),
 *     onEscape: () => closeAllModals()
 *   });
 *
 *   return <div>...</div>;
 * }
 */
